import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { HiFolderPlus } from 'react-icons/hi2';
import { setShowCreateFolderModal, addDirectory, setLoading } from '../../../redux/slices/storageSlice';
import { isValidFileName } from '../../../utils/storageUtils';
import storageService from '../../../services/storageService';
import Button from '../buttons/Button';

interface CreateFolderModalProps {
  isOpen: boolean;
  currentDirectoryId: number | null;
}

export default function CreateFolderModal({
  isOpen,
  currentDirectoryId,
}: CreateFolderModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [folderName, setFolderName] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!folderName.trim()) {
      setError(t('page.files.createFolderModal.errors.required'));
      return;
    }

    if (!isValidFileName(folderName)) {
      setError(t('page.files.createFolderModal.errors.invalidCharacters'));
      return;
    }

    try {
      dispatch(setLoading(true));
      const newDirectory = await storageService.createDirectory({
        name: folderName.trim(),
        parentId: currentDirectoryId,
      });
      dispatch(addDirectory(newDirectory));
      handleClose();
    } catch (err) {
      setError(t('page.files.createFolderModal.errors.createFailed'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleClose = () => {
    setFolderName('');
    setError('');
    dispatch(setShowCreateFolderModal(false));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-5 border-b border-secondary-200 bg-secondary-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <HiFolderPlus className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-semibold text-primary-900">{t('page.files.createFolderModal.title')}</h3>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div>
            <label htmlFor="folderName" className="block text-sm font-semibold text-primary-900 mb-2">
              {t('page.files.createFolderModal.folderName')}
            </label>
            <input
              type="text"
              id="folderName"
              value={folderName}
              onChange={(e) => {
                setFolderName(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-2.5 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
              placeholder={t('page.files.createFolderModal.placeholder')}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            {error && (
              <p className="mt-2 text-sm text-danger font-medium flex items-center gap-1">
                {error}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button
              type="button"
              variant="tertiary"
              onClick={handleClose}
            >
              {t('page.files.createFolderModal.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
            >
              {t('page.files.createFolderModal.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
